import React, { useEffect, useState } from 'react';
import {
  TabPanel,
  TabLabel,
  TabHead,
  TabProvider,
  Icon,
  IconButton
} from '@hevoio/hevo-ui-toolkit';
import { useHistory } from '@useHistory/useHistoryCompat';
import { NotificationService } from '../../../../core/services/notification.service';
import { EnvConfig } from '../../../../core/env-config';
import { NotificationsListType, NotificationsTab } from '../../interfaces';
import { DrawerWrapper } from '../../../../drawer/components/drawer-wrapper/DrawerWrapper';
import { ReactDrawerBaseProps } from '../../../../drawer/interfaces/drawer';
import { useHideDrawer } from '../../../../drawer/hooks/useHideDrawer';
import styles from './NotificationDrawer.module.scss';
import { StandardNotificationsList } from '../StandardNotificationsList';
import { EnterpriseNotificationsList } from '../EnterpriseNotificationsList';
import useTeamSettings from '../../../team/hooks/useTeamSettingsService';
import useService from '../../../../injector/hooks/useService';

export function NotificationDrawer({
  drawerDirection,
  disableRestoreFocus,
  usingReactRouter,
  closeInitiated,
  springValues
}: ReactDrawerBaseProps) {
  const history = useHistory();

  const [unreadCount, setUnreadCount] = useState<{ [key: string]: number }>({
    [NotificationsListType.REGULAR]: 0,
    [NotificationsListType.ENTERPRISE]: 0
  });
  const _teamSettingsService = useTeamSettings();
  const isEnterpriseEnabled =
    _teamSettingsService.isH2OEnabled() && EnvConfig.isH20FeatureEnabled('ALERTS');
  const _notificationService = useService(NotificationService);
  const { hideDrawer } = useHideDrawer();

  useEffect(() => {
    const unreadCountSubscription = _notificationService.unreadCount$
      .pipe()
      .subscribe(_unreadCount => {
        setUnreadCount({ ..._unreadCount });
      });
    return () => {
      unreadCountSubscription.unsubscribe();
    };
  }, []);

  const hide = () => {
    hideDrawer();
  };

  const redirectToAlerts = () => {
    history.push('/alerts');
  };

  return (
    <DrawerWrapper
      drawerDirection={drawerDirection}
      hide={hide}
      closeInitiated={closeInitiated}
      disableRestoreFocus={disableRestoreFocus}
      usingReactRouter={usingReactRouter}
      className={styles.drawer}
      springValues={springValues}
    >
      <div className={`${styles.drawerHeader} drawer-header`}>
        <div className='drawer-title center-flex-row'>
          Alerts

          <IconButton label='Settings' dataId='settings' className='ml-1' onClick={redirectToAlerts}>
            <Icon name='settings' size={2} />
          </IconButton>

          <IconButton label='Close' dataId='drawer-close' className='drawer-close' onClick={hide}>
            <Icon name='close' size={2} />
          </IconButton>
        </div>
      </div>

      <div className='drawer-body'>
        {isEnterpriseEnabled ? (
          <TabProvider defaultTab={NotificationsTab.REGULAR}>
            <TabHead isScrollable compact>
              <TabLabel
                compact
                key={NotificationsTab.REGULAR}
                value={NotificationsTab.REGULAR}
                data-id='regular-tab'
                label={
                  <>
                    <span>Standard</span>

                    {unreadCount[NotificationsListType.REGULAR] ? (
                      <div
                        data-id='unread-count-regular'
                        className='badge-solid error-badge badge-solid-md ml-3'
                      >
                        {unreadCount[NotificationsListType.REGULAR]}
                      </div>
                    ) : null}
                  </>
                }
              />

              <TabLabel
                compact
                key={NotificationsTab.ENTERPRISE}
                value={NotificationsTab.ENTERPRISE}
                className={`text-transform-none alerts-tab`}
                data-id='enterprise-tab'
                label={
                  <>
                    <span>Edge</span>

                    {unreadCount[NotificationsListType.ENTERPRISE] ? (
                      <div
                        data-id='unread-count-enterprise'
                        className='badge-solid error-badge badge-solid-md ml-3'
                      >
                        {unreadCount[NotificationsListType.ENTERPRISE]}
                      </div>
                    ) : null}
                  </>
                }
              />
            </TabHead>

            <TabPanel
              key={NotificationsTab.REGULAR}
              value={NotificationsTab.REGULAR}
              activeTabClassName='h-100'
            >
              <StandardNotificationsList />
            </TabPanel>

            <TabPanel
              key={NotificationsTab.ENTERPRISE}
              value={NotificationsTab.ENTERPRISE}
              activeTabClassName='h-100'
            >
              <EnterpriseNotificationsList />
            </TabPanel>
          </TabProvider>
        ) : null}
      </div>
    </DrawerWrapper>
  );
}
